import { isProtectedContent, isWithheldItem, MASK_CHAR } from '@components/previewUI/blocks/ContactsUI/utils/protect.js';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { sortItems } from '@utils/functions.js';

/**
 * Whether this content belongs to a block hidden from bots — data.protect, or an item Core marked
 * protected — and so may hold masks that must not be edited or saved back. The editor loads the raw
 * values from GET api/contact-blocks/{id}/private first (useContactsPrivate).
 *
 * Only the flags count, never a bullet on its own: an ordinary block may well say "Mon • Fri".
 */
export const needsContactsPrivate = (blockContent) => isProtectedContent(blockContent);

/**
 * Content of a protected block as Core sends it to a visitor: channels without their link, or masked
 * numbers and emails. A bullet is read as a mask only inside a block that is protected.
 */
const hasMaskedContent = (blockContent) =>
  isProtectedContent(blockContent) && ((blockContent?.contacts ?? []).some(isWithheldItem) || JSON.stringify(blockContent?.data ?? {}).includes(MASK_CHAR));

const initialState = {
  isLoading: false,
  // The raw contacts of this block have arrived from /private (or there was nothing to load).
  privateLoaded: false,
  // The last /private request failed; the editor stays closed and offers a retry.
  privateError: false,
  // Bumped to ask for /private again (the Retry button, the window regaining focus).
  privateRetry: 0,
  isActive: false,
  id: 0,
  uid: 0,
  sid: 0,

  blockContent: {
    buttonText: '',
    contacts: [],
    // Template content (people, place, hours, departments, form, brand, floating).
    // Stored as one JSON payload in Core, so a new template needs no new field here.
    data: {},
    heading: {
      title: '',
      description: '',
      textAlign: 'left',
      image: '',
      divider: '',
      position: '',
    },
  },
  blockDesign: {
    view: 'card',
  },
  buttonDesign: {},
  itemDesign: {},
  containerDesign: {},
};

export const contactsSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    contactsStore: (state, action) => {
      const { id, isActive, sid, uid, data } = action.payload;

      const block = data ?? {};
      const blockContent = block.blockContent ?? {};
      const blockDesign = block.blockDesign ?? {};
      const { buttonDesign, itemDesign, containerDesign } = blockDesign;
      const heading = blockContent.heading ?? {};

      state.isLoading = false;

      // Another block, or this one again with masked values (the page reloaded its sections):
      // the raw values have to be loaded again before anything is edited.
      if (state.id !== (id ?? 0) || hasMaskedContent(blockContent)) {
        state.privateLoaded = false;
        state.privateError = false;
      }

      state.id = id ?? 0;
      state.isActive = isActive;
      state.sid = sid;
      state.uid = uid;

      state.buttonDesign = {
        ...buttonDesign,
        backgroundColor: buttonDesign?.backgroundColor ?? '#000000',
        textColor: buttonDesign?.textColor ?? '#ffffff',
      };
      state.itemDesign = {
        shape: itemDesign?.shape ?? 'round',
        border: itemDesign?.border ?? 'none',
        borderColor: itemDesign?.borderColor ?? '#ECECF0',
        shadow: itemDesign?.shadow ?? 'none',
        shadowColor: itemDesign?.shadowColor ?? '#000000',
      };
      state.containerDesign = containerDesign ?? {};

      state.blockContent = {
        buttonText: blockContent.buttonText ?? '',
        contacts: blockContent.contacts ?? [],
        data: blockContent.data ?? {},
        heading: {
          ...heading,
          title: heading.title ?? '',
          description: heading.description ?? '',
          textAlign: heading.textAlign ?? 'left',
          image: heading.image ?? '',
          divider: heading.divider ?? '',
          position: heading.position ?? '',
        },
      };

      state.blockDesign = {
        view: blockDesign.view ?? 'card',
      };
    },
    contactsClear: () => initialState,

    contactsUpdateIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    contactsUpdateIsActive: (state, action) => {
      state.isActive = action.payload;
    },
    contactsUpdateBlockContent: (state, action) => {
      state.blockContent = action.payload;
    },
    contactsUpdateBlockDesign: (state, action) => {
      state.blockDesign = action.payload;
    },
    contactsSetItems: (state, action) => {
      state.blockContent.contacts = action.payload ?? [];
    },
    contactsUpdateData: (state, action) => {
      state.blockContent.data = action.payload ?? {};
    },
    contactsSetPrivateLoaded: (state, action) => {
      state.privateLoaded = action.payload;

      if (action.payload) {
        state.privateError = false;
      }
    },
    contactsSetPrivateError: (state, action) => {
      state.privateError = action.payload;
    },
    contactsRetryPrivate: (state) => {
      state.privateRetry += 1;
    },
  },
});

export const {
  contactsStore,
  contactsClear,
  contactsUpdateIsLoading,
  contactsUpdateIsActive,
  contactsUpdateBlockContent,
  contactsUpdateBlockDesign,
  contactsSetItems,
  contactsUpdateData,
  contactsSetPrivateLoaded,
  contactsSetPrivateError,
  contactsRetryPrivate,
} = contactsSlice.actions;

export const selectContactsIsLoading = (state) => state.contacts.isLoading;
export const selectContactsPrivateLoaded = (state) => state.contacts.privateLoaded;
export const selectContactsPrivateError = (state) => state.contacts.privateError;
export const selectContactsPrivateRetry = (state) => state.contacts.privateRetry;

export const selectContactsId = (state) => state.contacts.id;
export const selectContactsUID = (state) => state.contacts.uid;
export const selectContactsSID = (state) => state.contacts.sid;

export const selectContactsBlockContent = (state) => state.contacts.blockContent;
export const selectContactsData = (state) => state.contacts.blockContent.data;

// Recomputed only when the block content changes, not on every store update.
const selectContactsNeedsPrivate = createSelector([selectContactsBlockContent], needsContactsPrivate);

/**
 * Editing is open once the raw values are in — or straight away for a block that is not hidden
 * from bots, whose content already is the raw values, so it behaves exactly as before.
 */
export const selectContactsIsEditable = createSelector([selectContactsPrivateLoaded, selectContactsNeedsPrivate], (privateLoaded, needsPrivate) => privateLoaded || !needsPrivate);

export const selectContactsSortItems = createSelector([selectContactsBlockContent], ({ contacts }) => {
  return sortItems(contacts);
});

export const selectContactsBlockDesign = (state) => state.contacts.blockDesign;
export const selectContactsItemDesign = (state) => state.contacts.itemDesign;
export const selectContactsButtonDesign = (state) => state.contacts.buttonDesign;
export const selectContactsContainerDesign = (state) => state.contacts.containerDesign;

export default contactsSlice.reducer;
